import styled, { keyframes } from 'styled-components';

import { media } from './Theming.styles';
import { FRow } from './Primitives.styles';
import { giftCardStyles } from './VoucherShop.styles';

export const HomePage = styled.div`
	text-align: left;
`;

const rise = keyframes`
	to { transform: none; }
`;

export const Hero = styled.div`
	padding: calc(90px + 8vh) var(--pad) 0;

	h1 {
		font-size: clamp(54px, 9vw, 140px);
		line-height: 0.94;
	}

	/* the padding/margin pair gives diacritics (Ć, Č) headroom above the
	   0.94 line box so the overflow clip doesn't shave their accents off */
	h1 .row {
		display: block;
		overflow: hidden;
		padding-top: 0.14em;
		margin-top: -0.14em;
	}

	h1 .row span {
		display: inline-block;
		transform: translateY(110%);
		animation: ${rise} 1s cubic-bezier(0.2, 0.65, 0.2, 1) forwards;
	}

	h1 .row:nth-child(2) span {
		animation-delay: 0.12s;
	}

	h1 em {
		font-style: normal;
		color: var(--coral);
	}

	.sub {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 28px;
		flex-wrap: wrap;
		margin: 30px 0 44px;
	}

	.sub p {
		font-size: clamp(16px, 1.5vw, 21px);
		color: var(--brown);
		max-width: 420px;
		margin: 0;
	}

	.cta {
		display: flex;
		align-items: center;
		gap: 24px;
		flex-wrap: wrap;
	}

	.cta .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(25px, 2.3vw, 34px);
		transform: rotate(-2deg);
	}

	@media (max-width: 640px) {
		h1 {
			font-size: clamp(44px, 12.5vw, 78px);
		}

		/* on mobile the hand line moves down over the hero photo */
		.cta .hand {
			display: none;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		h1 .row span {
			transform: none;
			animation: none;
		}
	}
`;

export const HeroMedia = styled.div`
	margin: 0 var(--gut);
	position: relative;
	border-radius: var(--r);
	overflow: hidden;
	height: min(78vh, 760px);

	picture {
		display: contents;
	}

	img {
		width: 100%;
		height: 118%;
		object-fit: cover;
		object-position: center 28%;
		will-change: transform;
	}

	.shade-top {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 160px;
		z-index: 1;
		pointer-events: none;
		background: linear-gradient(rgba(24, 14, 2, 0.62), transparent);
	}

	.hand {
		display: none;
	}

	@media (max-width: 640px) {
		.hand {
			display: block;
			position: absolute;
			top: 22px;
			left: 22px;
			right: 22px;
			z-index: 2;
			font-family: var(--fh);
			font-weight: 600;
			color: var(--gold);
			font-size: 27px;
			transform: rotate(-2deg);
		}
	}

	.tag {
		position: absolute;
		left: 22px;
		bottom: 20px;
		background: ${({ theme }) =>
			theme.mode === 'dark' ? 'rgba(25,27,29,0.8)' : 'rgba(248,240,227,0.9)'};
		backdrop-filter: blur(8px);
		-webkit-backdrop-filter: blur(8px);
		border-radius: var(--r-ctl);
		padding: 10px 18px;
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 13.5px;
		font-weight: 600;
		color: ${({ theme }) => (theme.mode === 'dark' ? '#F7F7F7' : 'var(--ink)')};
	}

	.tag svg {
		color: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--teal)' : 'var(--teal-dark)'};
	}
`;

export const Kinetic = styled.div`
	padding: 96px 0 70px;
	overflow: hidden;

	.k-line {
		white-space: nowrap;
		font-family: var(--fd);
		font-size: clamp(44px, 6.5vw, 96px);
		text-transform: uppercase;
		line-height: 1.1;
		will-change: transform;
	}

	.k-line.k1 {
		color: var(--ink);
	}

	.k-line.k2 {
		color: transparent;
		-webkit-text-stroke: 1.5px
			${({ theme }) =>
				theme.mode === 'dark'
					? 'rgba(255,126,87,0.85)'
					: 'rgba(241,107,70,0.8)'};
		margin-top: 6px;
	}

	.k-line .dot {
		color: var(--coral);
		-webkit-text-stroke: 0;
		padding: 0 18px;
	}

	.k-line.k2 .dot {
		color: ${({ theme }) =>
			theme.mode === 'dark' ? 'rgba(255,126,87,0.85)' : 'rgba(241,107,70,0.8)'};
	}

	@media (max-width: 1079px) {
		.k-line {
			font-size: clamp(36px, 9vw, 64px);
		}
	}
`;

export const Formats = styled.div`
	padding: 20px 0 30px;
`;

export const TvBlock = styled.div`
	margin: 110px var(--gut) 0;
	position: relative;
	border-radius: var(--r);
	overflow: hidden;
	min-height: 92vh;
	display: flex;
	align-items: flex-end;
	outline: ${({ theme }) =>
		theme.mode === 'dark' ? '1px solid rgba(247,247,247,0.07)' : 'none'};
	outline-offset: -1px;

	img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 112%;
		object-fit: cover;
		will-change: transform;
	}

	.shade {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(24, 14, 2, 0.14),
			rgba(24, 14, 2, 0.05) 42%,
			rgba(24, 14, 2, 0.78) 100%
		);
	}

	.inner {
		position: relative;
		z-index: 2;
		padding: 0 clamp(22px, 4vw, 60px) 7vh;
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 30px;
		flex-wrap: wrap;
		width: 100%;
	}

	h2 {
		font-size: clamp(40px, 6vw, 92px);
		color: #fbf5ea;
		line-height: 0.98;
	}

	h2 em {
		font-style: normal;
		color: var(--teal);
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold);
		font-size: clamp(25px, 2.4vw, 35px);
		transform: rotate(-2deg);
	}
`;

export const Steps = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 0;
	padding: 26px var(--pad) 0;
	position: relative;

	.stp {
		position: relative;
		padding: 10px 26px 0 0;
	}

	.stp .n {
		font-family: var(--fd);
		font-size: clamp(56px, 6.5vw, 104px);
		line-height: 1;
	}

	.stp:nth-child(1) .n,
	.stp:nth-child(1) h3 {
		color: var(--teal-dark);
	}

	.stp:nth-child(2) .n,
	.stp:nth-child(2) h3 {
		color: var(--coral);
	}

	.stp:nth-child(3) .n,
	.stp:nth-child(3) h3 {
		color: var(--ink);
	}

	.stp:nth-child(4) .n,
	.stp:nth-child(4) h3 {
		color: var(--gold);
	}

	/* Caveat title needs a bigger px size than the Fjalla one it replaced —
	   its x-height is far smaller for the same font-size. */
	.stp h3 {
		font-family: var(--fh);
		font-weight: 700;
		text-transform: none;
		font-size: clamp(32px, 3vw, 44px);
		margin-top: 10px;
	}

	.stp .hand {
		display: block;
		font-family: 'Ubuntu Sans', sans-serif;
		font-weight: 500;
		font-size: clamp(16px, 1.3vw, 18px);
		color: var(--brown);
		margin-top: 8px;
		line-height: 1.5;
	}

	.stp svg.scrib {
		position: absolute;
		top: -8px;
		right: 26px;
		color: var(--coral);
		opacity: 0.9;
	}

	@media (max-width: 1079px) {
		grid-template-columns: 1fr 1fr;
		row-gap: 44px;
	}

	@media (max-width: 640px) {
		grid-template-columns: 1fr;

		.stp {
			padding-right: 0;
			text-align: center;
		}

		.stp svg.scrib {
			display: none;
		}
	}
`;

export const PaperCta = styled.div`
	padding: 70px var(--pad) 0;
	display: flex;
	align-items: center;
	gap: 28px;
	flex-wrap: wrap;
	position: relative;

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--coral);
		font-size: 26px;
	}

	@media (max-width: 640px) {
		flex-direction: column;
		justify-content: center;
		text-align: center;
		gap: 16px;
	}
`;

export const Covers = styled.div`
	padding: 110px 0 30px;
	overflow: hidden;

	/* desktop marquee carries the shadowed SaleCard tickets — leave room
	   below the reverse row so their drop shadows aren't cut off */
	${media.smallDesktop} {
		padding-bottom: 56px;
	}
`;

/**
 * One slot on the desktop marquee: the implemented SaleCard riding the
 * moving track. Margins instead of a track gap keep every slot the same
 * width, so the doubled content wraps seamlessly at exactly half the
 * track's scroll width.
 */
export const SaleSlot = styled.div`
	flex: 0 0 clamp(250px, 22vw, 320px);
	margin-right: 16px;
`;

/**
 * One ticket on the endless box-office roll the premade marquee unwinds:
 * cover in its native 3:2 with the title on the shade, then a low paper
 * coupon carrying the price and the author's signature. Tickets sit flush
 * against each other — the dashed right edge plus the punched notches at
 * its ends draw the junction where the next ticket begins, so the moving
 * track reads as one uncut roll (no outer radii: it enters and leaves
 * through the screen edge).
 */
export const RollCard = styled.a`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 0 0 clamp(250px, 24vw, 340px);
	text-decoration: none;
	text-align: left;
	background: var(--panel-2);
	cursor: pointer;
	border-right: 2px dashed rgba(var(--ink-rgb), 0.28);
	-webkit-mask-image: radial-gradient(
			circle 8px at 100% 0,
			transparent 8px,
			#000 8.5px
		),
		radial-gradient(circle 8px at 100% 100%, transparent 8px, #000 8.5px);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(circle 8px at 100% 0, transparent 8px, #000 8.5px),
		radial-gradient(circle 8px at 100% 100%, transparent 8px, #000 8.5px);
	mask-composite: intersect;

	.ph {
		position: relative;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		background: #17130f;
	}

	.ph img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s;
	}

	&:hover .ph img {
		transform: scale(1.04);
	}

	.ph .shade {
		position: absolute;
		inset: 40% 0 0 0;
		background: linear-gradient(
			180deg,
			rgba(24, 14, 2, 0),
			rgba(24, 14, 2, 0.78)
		);
	}

	.plate {
		position: absolute;
		inset: auto 0 0 0;
		z-index: 1;
		padding: 12px 16px;
	}

	.plate h3 {
		font-size: 20px;
		line-height: 1.08;
		color: #fff;
	}

	/* paper coupon: security lines, mono price label, the author signing */
	.stub {
		height: 50px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 0 16px;
		background-image: repeating-linear-gradient(
			45deg,
			rgba(var(--ink-rgb), 0.025) 0 1px,
			transparent 1px 8px
		);
	}

	.pr {
		display: flex;
		flex-direction: column;
		gap: 3px;
		min-width: 0;
	}

	.pr .lb {
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 8.5px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.pr .eur {
		font-family: var(--fd);
		font-size: 19px;
		line-height: 1;
		color: var(--coral);
		white-space: nowrap;
	}

	.by {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		min-width: 0;
	}

	.by .author-img {
		display: inline-flex;
		align-items: center;
	}

	.by .author-img img {
		display: block;
		height: 18px;
		width: auto;
	}

	.by .author-img.sq img {
		height: 22px;
	}

	.by .author-img .v-neon {
		display: none;
	}

	.by .author-tint {
		display: inline-flex;
		align-items: center;
	}

	.by .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 19px;
		line-height: 1;
		text-transform: none;
		color: var(--teal-dark);
		white-space: nowrap;
	}
`;

/* the quiz roll above already leaves room below its tickets */
export const GiftSection = styled.section`
	margin-top: 70px;
`;

const GIFT_STACK_WIDTH = 450;
const GIFT_STACK_HEIGHT = 262;
const GIFT_STACK_SCALE = 0.48;

/**
 * The gift promo: one taller row in the style of the quiz list, opening with
 * a small stack of gift cards that fans out on hover. The whole row links to
 * the shop.
 */
export const GiftPromo = styled(FRow)`
	${giftCardStyles}

	.in {
		grid-template-columns: ${GIFT_STACK_WIDTH * GIFT_STACK_SCALE}px 1fr auto auto;
		gap: 40px;
		padding: 56px var(--pad);
	}

	/* the /gift stack at a fraction of its size; the box keeps the scaled size */
	.thumb {
		display: block;
		width: ${GIFT_STACK_WIDTH * GIFT_STACK_SCALE}px;
		height: ${GIFT_STACK_HEIGHT * GIFT_STACK_SCALE}px;
		transition: transform 0.4s cubic-bezier(0.2, 0.65, 0.2, 1);
	}

	&:hover .thumb {
		transform: translateY(-6px);
	}

	.stack {
		display: block;
		width: ${GIFT_STACK_WIDTH}px;
		height: ${GIFT_STACK_HEIGHT}px;
		transform: scale(${GIFT_STACK_SCALE});
		transform-origin: 0 0;
	}

	.stack .vc {
		transition: transform 0.4s cubic-bezier(0.2, 0.65, 0.2, 1);
	}

	/* the back cards fan out a little more on hover */
	&:hover .stack .vc:nth-child(1) {
		transform: rotate(-8deg);
	}

	&:hover .stack .vc:nth-child(2) {
		transform: rotate(-3.5deg);
	}

	.thumb .vc small {
		display: none;
	}

	h3 {
		margin: 0;
	}

	.hand {
		display: block;
		width: max-content;
		max-width: 100%;
		margin-top: 8px;
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(24px, 2.6vw, 31px);
		line-height: 1.1;
		color: var(--gold-ink);
		transform: rotate(-2deg);
	}

	.desc {
		max-width: 250px;
	}

	@media (max-width: 1079px) {
		.in {
			grid-template-columns: ${GIFT_STACK_WIDTH * GIFT_STACK_SCALE}px 1fr auto;
		}
	}

	@media (max-width: 767px) {
		.in {
			grid-template-columns: 1fr auto;
			gap: 14px;
			padding: 42px var(--pad);
		}

		.thumb {
			display: none;
		}

		h3 {
			font-size: 34px;
		}
	}
`;

export const Finale = styled.div`
	padding: 150px var(--pad) 130px;
	text-align: center;

	h2 {
		font-size: clamp(44px, 7vw, 110px);
		line-height: 0.98;
	}

	h2 em {
		font-style: normal;
		color: var(--coral);
	}

	.hand {
		display: block;
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(25px, 2.6vw, 37px);
		margin: 26px 0 40px;
		transform: rotate(-1.6deg);
	}
`;
